import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    User, ShieldCheck, ChevronRight, Edit3, Bell, LogOut, Phone, MapPin, Mail,
    Home as HomeIcon, Bookmark, MessageSquare, Star,
    CreditCard, Crown, Rocket, ShieldAlert, Wallet as WalletIcon,
    HelpCircle, Gift, Settings, Lock, Video, Menu, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, fileUrl } from "../lib/api";
import { isServiceRole, isListingRole, isAdmin } from "../lib/roles";
import AdminMobileConsole from "./AdminMobileConsole";

/* ==============================================================
   GPM PROFILE PAGE — matches the provided reference mockup.
   Layout: hero → 4-cell "Dashboard Overview" (Refer & Earn highlighted)
           → 4-cell "Manage Business" → "Account" → "Support & Settings".
   Uses only existing routes and existing backend counts.
   ============================================================== */

/* ---------- Anon state ---------- */
function AnonProfile() {
    return (
        <div className="min-h-[70vh] bg-[#f6f7fb] flex flex-col items-center justify-center px-6 text-center" data-testid="profile-anon">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(0,38,87,0.08)" }}>
                <User className="w-10 h-10" style={{ color: "rgba(0,38,87,0.6)" }} />
            </div>
            <h1 className="font-heading font-bold text-xl mb-1" style={{ color: "#002657" }}>Log in to Global Property Mart</h1>
            <p className="text-sm max-w-xs" style={{ color: "rgba(0,38,87,0.6)" }}>Save properties, track enquiries, chat with brokers and more.</p>
            <div className="flex gap-2 mt-6 w-full max-w-xs">
                <Link to="/login?next=/profile" data-testid="profile-login" className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-extrabold" style={{ backgroundColor: "#002657", color: "#fff" }}>Log In</Link>
                <Link to="/register?next=/profile" data-testid="profile-signup" className="flex-1 inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-extrabold" style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}>Sign Up</Link>
            </div>
        </div>
    );
}

/* ---------- Tile component (icon + label, tappable) ---------- */
function Tile({ icon: Icon, label, sub, value, to, tint, highlight = false, testid }) {
    // Gold-highlighted variant is used exclusively for Refer & Earn — visually more prominent.
    const outer = highlight
        ? "relative rounded-2xl p-3 border-2 flex flex-col items-start justify-between min-h-[110px] active:scale-[0.98] transition-transform overflow-hidden"
        : "rounded-2xl p-3 border flex flex-col items-start justify-between min-h-[110px] active:scale-[0.97] transition-transform";
    const outerStyle = highlight
        ? { backgroundColor: "#FEF3C7", borderColor: "#DAA520", boxShadow: "0 4px 14px rgba(218,165,32,0.25)" }
        : { backgroundColor: tint.bg, borderColor: "rgba(0,38,87,0.06)" };
    const iconWrap = highlight
        ? "w-9 h-9 rounded-xl flex items-center justify-center"
        : "w-9 h-9 rounded-xl flex items-center justify-center";
    const iconStyle = highlight
        ? { backgroundColor: "#F59E0B26" }
        : { backgroundColor: tint.iconBg || "#ffffff80" };
    const iconColor = highlight ? "#B45309" : tint.fg;
    const labelColor = highlight ? "#78350F" : "#002657";
    const subColor = highlight ? "#B45309" : "rgba(0,38,87,0.55)";
    return (
        <Link to={to} data-testid={testid} className={outer} style={outerStyle}>
            <div className="flex items-start justify-between w-full">
                <div className={iconWrap} style={iconStyle}>
                    <Icon className="w-5 h-5" style={{ color: iconColor }} strokeWidth={2.2} />
                </div>
                {highlight && (
                    <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ backgroundColor: "#DAA520" }}>
                        <ArrowRight className="w-4 h-4" style={{ color: "#1F1300" }} />
                    </div>
                )}
            </div>
            <div className="mt-2 w-full">
                {value !== undefined && (
                    <div className="font-heading font-extrabold text-2xl leading-none mb-1" style={{ color: labelColor }}>{value}</div>
                )}
                <div className={`font-extrabold leading-tight ${highlight ? "text-[13px]" : "text-[10px] uppercase tracking-wider"}`} style={{ color: labelColor }}>{label}</div>
                {sub && <div className="text-[10px] font-semibold mt-0.5 leading-tight" style={{ color: subColor }}>{sub}</div>}
            </div>
        </Link>
    );
}

/* ---------- Section wrapper with heading + optional "View All" ---------- */
function Section({ title, viewAllTo, testid, children }) {
    return (
        <section className="mt-4 mx-3" data-testid={testid}>
            <div className="rounded-2xl bg-white p-3 sm:p-4 shadow-sm border" style={{ borderColor: "rgba(0,38,87,0.06)" }}>
                <div className="flex items-center justify-between px-1 mb-3">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold" style={{ color: "#002657" }}>{title}</div>
                    {viewAllTo && (
                        <Link to={viewAllTo} className="inline-flex items-center gap-1 text-[11px] font-bold" style={{ color: "#B45309" }} data-testid={`${testid}-viewall`}>
                            View All <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                    )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {children}
                </div>
            </div>
        </section>
    );
}

/* ---------- Main ---------- */
export default function Profile() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState({ properties: 0, enquiries: 0, rating: "0.0" });

    useEffect(() => {
        if (!user) return;
        (async () => {
            const [propsRes, inqRes, revRes] = await Promise.all([
                api.get("/me/properties").then((r) => r.data?.items || r.data || []).catch(() => []),
                api.get("/inquiries/mine").then((r) => r.data?.items || r.data || []).catch(() => []),
                api.get("/reviews", { params: { target_id: user.id, limit: 100 } }).then((r) => r.data || []).catch(() => []),
            ]);
            const avg = revRes.length ? (revRes.reduce((s, r) => s + (r.rating || 0), 0) / revRes.length) : 0;
            setStats({
                properties: propsRes.length,
                enquiries: inqRes.length,
                rating: Number(avg || 0).toFixed(1),
            });
        })();
    }, [user]);

    if (!user) return <AnonProfile />;
    // Admins get a dedicated Admin Console (never the user Profile UI).
    if (isAdmin(user.role)) return <AdminMobileConsole />;

    const onLogout = () => {
        logout();
        toast.success("Logged out");
        navigate("/");
    };

    const initials = (user.name || user.email || "U").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
    const displayCity = [user.locality, user.city, user.state].filter(Boolean).slice(0, 2).join(", ") || "—";
    const isPartner = user.role === "gpm_partner" && user.partner_status === "active";
    const isPremium = isPartner || !!user.premium_expires_at;
    const premiumTill = user.partner_expires_at || user.premium_expires_at || user.plan_expires_at;

    // Colour palette for tiles — matches the reference mockup.
    const T = {
        green:   { bg: "#DCFCE7", iconBg: "#BBF7D0", fg: "#16A34A" },
        pink:    { bg: "#FCE7F3", iconBg: "#FBCFE8", fg: "#DB2777" },
        rose:    { bg: "#FFE4E6", iconBg: "#FECDD3", fg: "#E11D48" },
        peach:   { bg: "#FED7AA", iconBg: "#FDBA74", fg: "#EA580C" },
        purple:  { bg: "#EDE9FE", iconBg: "#DDD6FE", fg: "#7C3AED" },
        indigo:  { bg: "#E0E7FF", iconBg: "#C7D2FE", fg: "#4F46E5" },
        blue:    { bg: "#DBEAFE", iconBg: "#BFDBFE", fg: "#2563EB" },
        amber:   { bg: "#FEF3C7", iconBg: "#FDE68A", fg: "#D97706" },
        emerald: { bg: "#D1FAE5", iconBg: "#A7F3D0", fg: "#059669" },
        slate:   { bg: "#F1F5F9", iconBg: "#E2E8F0", fg: "#475569" },
    };

    // Role gating for Manage Business — buyers get a slimmer default set.
    const isPro = isListingRole(user.role) || isServiceRole(user.role) || isPartner;

    return (
        <div className="min-h-screen bg-[#f6f7fb] pb-8" data-testid="profile-page">
            {/* =============== HERO =============== */}
            <div
                className="text-on-image px-4 pt-3 pb-6 relative overflow-hidden"
                style={{ background: "linear-gradient(180deg, #0A1229 0%, #002657 100%)" }}
                data-testid="profile-hero"
            >
                <div aria-hidden className="absolute inset-0 pointer-events-none opacity-25" style={{ background: "radial-gradient(ellipse 400px 200px at 90% 0%, rgba(218,165,32,0.5), transparent 60%)" }} />

                {/* Utility row — notifications + hamburger */}
                <div className="relative flex items-center justify-end gap-2 mb-3">
                    <Link to="/notifications" data-testid="profile-notif" aria-label="Notifications" className="w-9 h-9 rounded-full bg-white/12 backdrop-blur-md border border-white/20 flex items-center justify-center relative">
                        <Bell className="w-4 h-4 text-white" />
                        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-rose-500" />
                    </Link>
                    <Link to="/account-settings" data-testid="profile-menu" aria-label="Menu" className="w-9 h-9 rounded-full bg-white/12 backdrop-blur-md border border-white/20 flex items-center justify-center">
                        <Menu className="w-4 h-4 text-white" />
                    </Link>
                </div>

                {/* Identity + Edit + Premium */}
                <div className="relative flex items-start gap-4">
                    <div className="relative shrink-0">
                        {user.photo ? (
                            <img src={fileUrl(user.photo)} alt={user.name} className="w-20 h-20 rounded-full object-cover border-4" style={{ borderColor: "rgba(255,255,255,0.15)" }} />
                        ) : (
                            <div className="w-20 h-20 rounded-full flex items-center justify-center text-3xl font-heading font-extrabold border-4 shadow-lg" style={{ background: "linear-gradient(135deg, #DAA520, #F59E0B)", color: "#001a3d", borderColor: "rgba(255,255,255,0.15)" }} data-testid="profile-avatar">
                                {initials}
                            </div>
                        )}
                        {user.verified && (
                            <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-emerald-500 border-2 flex items-center justify-center" style={{ borderColor: "#0A1229" }} data-testid="profile-verified-badge">
                                <ShieldCheck className="w-3.5 h-3.5 text-white" />
                            </div>
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                            <div className="font-heading font-extrabold text-white text-lg truncate" data-testid="profile-name">{user.name || "Member"}</div>
                            {user.verified && <ShieldCheck className="w-4 h-4 shrink-0" style={{ color: "#34D399" }} />}
                        </div>
                        <div className="text-white/85 text-[11px] flex items-center gap-1.5 truncate mt-1">
                            <Mail className="w-3 h-3 shrink-0" /> <span className="truncate" data-testid="profile-email">{user.email}</span>
                        </div>
                        {user.phone && (
                            <div className="text-white/85 text-[11px] flex items-center gap-1.5 mt-0.5" data-testid="profile-phone">
                                <Phone className="w-3 h-3 shrink-0" /> {String(user.phone).startsWith("+") ? user.phone : `+91 ${user.phone}`}
                            </div>
                        )}
                        <div className="text-[11px] flex items-center gap-1.5 mt-0.5" style={{ color: "#F5E5A5" }} data-testid="profile-city">
                            <MapPin className="w-3 h-3 shrink-0" /> {displayCity}
                        </div>
                    </div>
                </div>

                {/* Action row — Edit Profile + Premium Member card */}
                <div className="relative mt-3 flex items-center gap-2">
                    <Link to="/account-settings" data-testid="profile-edit-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-extrabold shadow" style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}>
                        <Edit3 className="w-3.5 h-3.5" /> Edit Profile
                    </Link>
                    {isPremium && (
                        <Link to={isPartner ? "/partner/dashboard" : "/my-subscriptions"} data-testid="profile-premium" className="ml-auto inline-flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border border-gold-500/40 bg-white/8 backdrop-blur-md">
                            <Crown className="w-4 h-4" style={{ color: "#F5E5A5" }} />
                            <div className="text-right leading-tight">
                                <div className="text-[11px] font-extrabold text-white">Premium Member</div>
                                {premiumTill && <div className="text-[9px] font-semibold" style={{ color: "#F5E5A5" }}>Valid till {new Date(premiumTill).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>}
                            </div>
                            <ChevronRight className="w-3.5 h-3.5" style={{ color: "#F5E5A5" }} />
                        </Link>
                    )}
                </div>
            </div>

            {/* =============== DASHBOARD OVERVIEW =============== */}
            <Section title="Dashboard Overview" testid="section-overview">
                <Tile icon={HomeIcon} label="Properties" value={String(stats.properties).padStart(2, "0")} to="/my-properties" tint={T.green} testid="ov-properties" />
                <Tile icon={Gift} label="Refer & Earn" sub="Invite Friends Earn Rewards" to="/refer" highlight testid="ov-refer" />
                <Tile icon={MessageSquare} label="Enquiries" value={String(stats.enquiries).padStart(2, "0")} to="/my-inquiries" tint={T.blue} testid="ov-enquiries" />
                <Tile icon={Star} label="Rating" value={String(stats.rating)} to="/my-reviews" tint={T.amber} testid="ov-rating" />
            </Section>

            {/* =============== MANAGE BUSINESS =============== */}
            <Section title="Manage Business" testid="section-business">
                <Tile icon={HomeIcon} label="My Properties" to="/my-properties" tint={T.green} testid="biz-properties" />
                <Tile icon={Video} label="My Reels" to="/my-reels" tint={T.rose} testid="biz-reels" />
                <Tile icon={Rocket} label="My Boosts" to="/my-boosts" tint={T.peach} testid="biz-boosts" />
                <Tile icon={Bookmark} label="My Bookmarks" to="/saved" tint={T.purple} testid="biz-bookmarks" />
            </Section>

            {/* =============== ACCOUNT =============== */}
            <Section title="Account" testid="section-account">
                <Tile icon={CreditCard} label="My Payments" to="/my-payments" tint={T.blue} testid="acc-payments" />
                <Tile icon={Crown} label="My Subscriptions" to="/my-subscriptions" tint={T.purple} testid="acc-subs" />
                <Tile icon={WalletIcon} label="My Wallet" to="/wallet" tint={T.amber} testid="acc-wallet" />
                <Tile icon={ShieldAlert} label="KYC Verification" to="/kyc" tint={T.emerald} testid="acc-kyc" />
            </Section>

            {/* =============== SUPPORT & SETTINGS =============== */}
            <Section title="Support & Settings" testid="section-support">
                <Tile icon={HelpCircle} label="Help & Support" to="/contact" tint={T.blue} testid="sup-help" />
                <Tile icon={Bell} label="Notifications" to="/notifications" tint={T.indigo} testid="sup-notifications" />
                <Tile icon={Settings} label="Account Settings" to="/account-settings" tint={T.slate} testid="sup-settings" />
                <Tile icon={Lock} label="Privacy & Security" to="/privacy" tint={T.emerald} testid="sup-privacy" />
            </Section>

            {/* Logout */}
            <div className="mx-3 mt-4">
                <button type="button" onClick={onLogout} data-testid="profile-logout" className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-white border font-bold text-[13px]" style={{ borderColor: "rgba(220,38,38,0.2)", color: "#DC2626" }}>
                    <LogOut className="w-4 h-4" /> Log out
                </button>
            </div>

            <div className="text-center py-5 text-[10px] uppercase tracking-widest" style={{ color: "rgba(0,38,87,0.4)" }}>
                Global Property Mart · v1.0
            </div>
        </div>
    );
}
